<template>
  <div class="sms-admin">
    <!-- Top Bar -->
    <div class="topbar">
      <div class="topbar-left">
        <h1>SMS</h1>
        <span class="topbar-sep">/</span>
        <span class="topbar-crumb">Plages horaires</span>
      </div>
      <div class="topbar-right">
        <div class="topbar-stats">
          <span class="stat-block">
            <span class="stat-val">{{ (profile.clients || []).length }}</span>
            <span class="stat-lbl">clients</span>
          </span>
          <span class="stat-dot">&middot;</span>
          <span class="stat-block">
            <span class="stat-val">{{ totalSites }}</span>
            <span class="stat-lbl">univers</span>
          </span>
          <span class="stat-dot">&middot;</span>
          <span class="stat-block">
            <span class="stat-val">{{ allUsers.length }}</span>
            <span class="stat-lbl">utilisateurs</span>
          </span>
          <span class="stat-dot">&middot;</span>
          <span class="stat-block stat-highlight">
            <span class="stat-val">{{ totalSmsSent.toLocaleString('fr-FR') }}</span>
            <span class="stat-lbl">SMS</span>
          </span>
        </div>
        <button class="icon-btn" @click="refresh" :disabled="loading" title="Rafraîchir">
          <span class="material-symbols-outlined" :class="{ spinning: loading }">refresh</span>
        </button>
      </div>
    </div>

    <!-- Dual Panel -->
    <main class="dual-panel" v-if="dataReady">
      <!-- LEFT: Clients & Sites -->
      <aside class="panel panel-left">
        <div class="panel-header">
          <span class="material-symbols-outlined panel-icon">business</span>
          <span class="panel-title">Clients & Univers</span>
          <span class="panel-count">{{ filteredClients.length }}</span>
        </div>
        <div class="panel-search">
          <span class="material-symbols-outlined si">search</span>
          <input v-model="clientSearch" type="text" placeholder="Rechercher client / univers..." />
          <button v-if="clientSearch" class="search-x" @click="clientSearch = ''">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="panel-body">
          <div
            v-for="client in filteredClients"
            :key="client.id"
            class="client-block"
            :class="{ selected: selectedClientId === client.id }"
          >
            <button class="client-row" @click="selectClient(client.id)">
              <span class="avatar-sm">{{ getInitials(client.name) }}</span>
              <div class="client-info">
                <span class="client-name">{{ client.name }}</span>
                <span class="client-meta">
                  {{ getClientSites(client.id).length }} univers &middot;
                  {{ getClientUsers(client.id).length }} util. &middot;
                  {{ getClientSmsCount(client.id).toLocaleString('fr-FR') }} SMS
                </span>
              </div>
              <span class="config-pill" :class="getClientConfigStatus(client.id)">
                {{ getClientConfiguredUserCount(client.id) }}/{{ getClientUsers(client.id).length }}
              </span>
              <span class="material-symbols-outlined chevron" :class="{ open: expandedClients.includes(client.id) }">chevron_right</span>
            </button>

            <transition name="slide">
              <div v-if="expandedClients.includes(client.id)" class="sites-list">
                <div
                  v-for="site in getClientSites(client.id)"
                  :key="site.id"
                  class="site-row"
                >
                  <span class="material-symbols-outlined site-icon">dns</span>
                  <span class="site-name">{{ site.name }}</span>
                  <span class="site-sms">{{ getSiteSmsCount(site.id).toLocaleString('fr-FR') }} SMS</span>
                </div>
                <div v-if="getClientSites(client.id).length === 0" class="empty-row">Aucun univers</div>
              </div>
            </transition>
          </div>

          <div v-if="filteredClients.length === 0" class="no-results-sm">
            <span class="material-symbols-outlined">search_off</span>
            <span>Aucun résultat</span>
          </div>
        </div>
      </aside>

      <!-- RIGHT: All Users -->
      <section class="panel panel-right">
        <div class="panel-header">
          <span class="material-symbols-outlined panel-icon">group</span>
          <span class="panel-title">Utilisateurs SMS</span>
          <span class="panel-count">{{ filteredUsers.length }}</span>
          <div class="panel-actions">
            <button
              class="filter-chip"
              :class="{ active: userFilter === null }"
              @click="userFilter = null"
            >Tous</button>
            <button
              class="filter-chip"
              :class="{ active: userFilter === 'configured' }"
              @click="userFilter = 'configured'"
            >Configurés</button>
            <button
              class="filter-chip"
              :class="{ active: userFilter === 'pending' }"
              @click="userFilter = 'pending'"
            >En attente</button>
          </div>
        </div>
        <div class="panel-search">
          <span class="material-symbols-outlined si">search</span>
          <input v-model="userSearch" type="text" placeholder="Rechercher utilisateur..." />
          <button v-if="userSearch" class="search-x" @click="userSearch = ''">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="panel-body">
          <table class="user-table" v-if="filteredUsers.length > 0">
            <thead>
              <tr>
                <th class="th-user">Utilisateur</th>
                <th class="th-clients">Clients</th>
                <th class="th-sms">SMS</th>
                <th class="th-start">Début</th>
                <th class="th-end">Fin</th>
                <th class="th-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in filteredUsers"
                :key="user.id"
                :class="{ modified: hasUnsavedChanges(user.id) }"
              >
                <td class="td-user">
                  <span class="user-dot" :class="{ active: isUserConfigured(user.id) }"></span>
                  <div class="user-id">
                    <span class="user-name">{{ user.firstname }} {{ user.lastname }}</span>
                    <span class="user-email">{{ user.email }}</span>
                  </div>
                </td>
                <td class="td-clients">
                  <div class="client-tags">
                    <span
                      v-for="cId in user.clientIds"
                      :key="cId"
                      class="client-tag"
                      :class="{ highlight: cId === selectedClientId }"
                    >{{ getClientName(cId) }}</span>
                  </div>
                </td>
                <td class="td-sms">
                  <span class="sms-count">{{ getUserTotalSms(user.id).toLocaleString('fr-FR') }}</span>
                </td>
                <td class="td-time">
                  <input
                    type="time"
                    class="time-input"
                    :value="getUserConfig(user.id).startHour"
                    @change="updateUserConfig(user.id, 'startHour', $event.target.value)"
                    :disabled="savingStates[user.id]"
                  />
                </td>
                <td class="td-time">
                  <input
                    type="time"
                    class="time-input"
                    :value="getUserConfig(user.id).endHour"
                    @change="updateUserConfig(user.id, 'endHour', $event.target.value)"
                    :disabled="savingStates[user.id]"
                  />
                </td>
                <td class="td-actions">
                  <button
                    class="btn-save"
                    @click.stop="saveUserConfig(user.id)"
                    :disabled="savingStates[user.id] || !hasUnsavedChanges(user.id)"
                  >
                    <span class="material-symbols-outlined" :class="{ spinning: savingStates[user.id] }">{{ savingStates[user.id] ? 'sync' : 'check' }}</span>
                  </button>
                  <button
                    class="btn-reset"
                    @click.stop="resetUserConfig(user.id)"
                    :disabled="savingStates[user.id]"
                  >
                    <span class="material-symbols-outlined">undo</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else class="no-results-sm">
            <span class="material-symbols-outlined">person_off</span>
            <span>Aucun utilisateur trouvé</span>
            <button v-if="userSearch || userFilter" class="btn-text" @click="userSearch = ''; userFilter = null">Effacer les filtres</button>
          </div>
        </div>
      </section>
    </main>

    <!-- Loading -->
    <main class="dual-panel loading-state" v-if="!dataReady">
      <div class="loader">
        <div class="loader-spinner"></div>
        <span>Chargement...</span>
      </div>
    </main>

    <!-- Toasts -->
    <transition-group name="toast-anim" tag="div" class="toast-stack">
      <div class="toast" v-for="toast in toasts" :key="toast.id" :class="toast.type">
        <span class="material-symbols-outlined toast-icon">{{ toast.icon }}</span>
        <span class="toast-msg">{{ toast.message }}</span>
        <button class="toast-dismiss" @click="removeToast(toast.id)">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: 'SmsManagement',
  data() {
    return {
      profile: this.$store.state.profile.user,
      loading: false,
      dataReady: false,
      clientSearch: '',
      userSearch: '',
      userFilter: null,
      selectedClientId: null,
      expandedClients: [],
      userConfigs: {},
      originalConfigs: {},
      smsStats: {},
      savingStates: {},
      toasts: [],
    };
  },

  computed: {
    filteredClients() {
      const clients = this.profile.clients || [];
      if (!this.clientSearch.trim()) return clients;
      const q = this.clientSearch.toLowerCase().trim();
      return clients.filter(c => {
        if ((c.name || '').toLowerCase().includes(q)) return true;
        return this.getClientSites(c.id).some(s => (s.name || '').toLowerCase().includes(q));
      });
    },

    totalSites() {
      return (this.profile.sites || []).length;
    },

    allUsers() {
      const map = new Map();
      (this.profile.clients || []).forEach(client => {
        this.getClientSites(client.id).forEach(site => {
          (site.users || []).forEach(user => {
            if (user.optin_sms != 1) return;
            if (map.has(user.id)) {
              map.get(user.id).clientIds.add(client.id);
            } else {
              const u = { ...user, clientIds: new Set([client.id]) };
              map.set(user.id, u);
            }
          });
        });
      });
      return Array.from(map.values()).map(u => ({
        ...u,
        clientIds: Array.from(u.clientIds),
      }));
    },

    filteredUsers() {
      let list = this.allUsers;

      if (this.selectedClientId !== null) {
        list = list.filter(u => u.clientIds.includes(this.selectedClientId));
      }

      if (this.userFilter === 'configured') {
        list = list.filter(u => this.isUserConfigured(u.id));
      } else if (this.userFilter === 'pending') {
        list = list.filter(u => !this.isUserConfigured(u.id));
      }

      if (this.userSearch.trim()) {
        const q = this.userSearch.toLowerCase().trim();
        list = list.filter(u => {
          const full = `${u.firstname || ''} ${u.lastname || ''}`.toLowerCase();
          const email = (u.email || '').toLowerCase();
          return full.includes(q) || email.includes(q) || String(u.id).includes(q);
        });
      }

      return list;
    },

    totalSmsSent() {
      let total = 0;
      Object.entries(this.smsStats).forEach(([key, count]) => {
        if (key.startsWith('site-')) total += count || 0;
      });
      return total;
    },
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        await this.$store.dispatch('profileRequest');
        this.profile = this.$store.state.profile.user;
        this.initializeConfigs();
        this.dataReady = true;
      } catch (e) {
        console.error('Error:', e);
        this.showToast('error', 'Erreur lors du chargement');
      } finally {
        this.loading = false;
      }
    },

    initializeConfigs() {
      const handled = new Set();

      (this.profile.clients || []).forEach(client => {
        this.getClientSites(client.id).forEach(site => {
          (site.users || []).forEach(user => {
            if (user.optin_sms != 1 || handled.has(user.id)) return;
            handled.add(user.id);

            const existing =
              user.sms_config ||
              user.sms_configs?.[client.id] ||
              user.sms_config_by_client?.[client.id] ||
              {};

            this.$set(this.userConfigs, user.id, {
              startHour: existing.start_hour || existing.startHour || '06:00',
              endHour: existing.end_hour || existing.endHour || '22:00',
            });

            this.$set(this.originalConfigs, user.id, {
              startHour: existing.start_hour || existing.startHour || '06:00',
              endHour: existing.end_hour || existing.endHour || '22:00',
            });

            this.$set(this.smsStats, `user-${user.id}`, user.sms_count || 0);
          });
        });
      });

      (this.profile.sites || []).forEach(site => {
        this.$set(this.smsStats, `site-${site.id}`, site.sms_count || 0);
      });
    },

    refresh() {
      this.dataReady = false;
      this.userConfigs = {};
      this.originalConfigs = {};
      this.smsStats = {};
      this.expandedClients = [];
      this.selectedClientId = null;
      this.loadData();
    },

    getInitials(name) {
      if (!name) return '?';
      const words = name.split(' ').filter(Boolean);
      return words.length >= 2
        ? `${words[0][0]}${words[1][0]}`.toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },

    getClientSites(clientId) {
      return (this.profile.sites || []).filter(s => s.client_id === clientId);
    },

    getClientUsers(clientId) {
      const map = new Map();
      this.getClientSites(clientId).forEach(site => {
        (site.users || []).forEach(user => {
          if (!map.has(user.id)) map.set(user.id, user);
        });
      });
      return Array.from(map.values()).filter(u => u.optin_sms == 1);
    },

    getClientSmsCount(clientId) {
      return this.getClientSites(clientId).reduce((sum, site) => {
        return sum + (this.smsStats[`site-${site.id}`] || 0);
      }, 0);
    },

    getClientConfiguredUserCount(clientId) {
      return this.getClientUsers(clientId).filter(u => this.isUserConfigured(u.id)).length;
    },

    getClientConfigStatus(clientId) {
      const users = this.getClientUsers(clientId);
      if (users.length === 0) return 'pending';
      const configured = this.getClientConfiguredUserCount(clientId);
      if (configured === users.length) return 'configured';
      if (configured > 0) return 'partial';
      return 'pending';
    },

    getSiteSmsCount(siteId) {
      return this.smsStats[`site-${siteId}`] || 0;
    },

    getClientName(clientId) {
      const c = (this.profile.clients || []).find(cl => cl.id === clientId);
      return c ? c.name : `#${clientId}`;
    },

    selectClient(clientId) {
      const idx = this.expandedClients.indexOf(clientId);
      if (idx > -1) {
        this.expandedClients.splice(idx, 1);
      } else {
        this.expandedClients.push(clientId);
      }

      if (this.selectedClientId === clientId) {
        this.selectedClientId = null;
      } else {
        this.selectedClientId = clientId;
      }
    },

    getUserConfig(userId) {
      return this.userConfigs[userId] || { startHour: '', endHour: '' };
    },

    isUserConfigured(userId) {
      const cfg = this.getUserConfig(userId);
      return !!(cfg.startHour && cfg.endHour);
    },

    getUserTotalSms(userId) {
      return this.smsStats[`user-${userId}`] || 0;
    },

    updateUserConfig(userId, field, value) {
      if (!this.userConfigs[userId]) {
        this.$set(this.userConfigs, userId, { startHour: '', endHour: '' });
      }
      this.$set(this.userConfigs[userId], field, value);
    },

    hasUnsavedChanges(userId) {
      const current = this.userConfigs[userId] || {};
      const original = this.originalConfigs[userId] || {};
      return current.startHour !== original.startHour || current.endHour !== original.endHour;
    },

    async saveUserConfig(userId) {
      this.$set(this.savingStates, userId, true);
      try {
        const config = this.userConfigs[userId];
        await this.$store.dispatch('updateUserSmsConfig', {
          userId,
          startHour: config.startHour,
          endHour: config.endHour,
        });
        this.$set(this.originalConfigs, userId, { ...config });
        this.showToast('success', 'Configuration sauvegardée');
      } catch (e) {
        console.error('Error:', e);
        this.showToast('error', 'Erreur lors de la sauvegarde');
      } finally {
        this.$set(this.savingStates, userId, false);
      }
    },

    resetUserConfig(userId) {
      const original = this.originalConfigs[userId] || { startHour: '', endHour: '' };
      this.$set(this.userConfigs, userId, { ...original });
      this.showToast('info', 'Configuration réinitialisée');
    },

    showToast(type, message) {
      const icons = { success: 'check_circle', error: 'error', warning: 'warning', info: 'info' };
      const toast = { id: `${Date.now()}-${Math.random()}`, type, message, icon: icons[type] || 'info' };
      this.toasts.push(toast);
      setTimeout(() => this.removeToast(toast.id), 4000);
    },

    removeToast(id) {
      const i = this.toasts.findIndex(t => t.id === id);
      if (i > -1) this.toasts.splice(i, 1);
    },
  },
};
</script>

<style scoped>
/* ================================================================
   Foundation
   ================================================================ */
.sms-admin {
  --c-bg: #f7f8f9;
  --c-surface: #ffffff;
  --c-border: #e4e7eb;
  --c-border-light: #eef0f3;
  --c-text: #111827;
  --c-text-2: #4b5563;
  --c-text-3: #9ca3af;
  --c-primary: #16a34a;
  --c-primary-soft: rgba(22, 163, 74, 0.08);
  --c-primary-muted: rgba(22, 163, 74, 0.15);
  --c-danger: #ef4444;
  --c-warn: #f59e0b;
  --c-info: #3b82f6;
  --radius: 8px;
  --mono: 'SF Mono', 'Cascadia Code', 'Roboto Mono', monospace;

  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--c-bg);
  color: var(--c-text);
  -webkit-font-smoothing: antialiased;
}

p { margin: 0 !important; }

/* ================================================================
   Top bar
   ================================================================ */
.topbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 0 28px;
  height: 56px;
  background: var(--c-surface);
  border-bottom: 1px solid var(--c-border);
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.topbar-left h1 {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.topbar-sep {
  color: var(--c-text-3);
  font-weight: 300;
  font-size: 1.125rem;
}

.topbar-crumb {
  color: var(--c-text-2);
  font-size: 0.875rem;
  font-weight: 500;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.topbar-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
}

.stat-block {
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.stat-val {
  font-weight: 600;
  color: var(--c-text-2);
}

.stat-lbl {
  color: var(--c-text-3);
  font-weight: 400;
}

.stat-dot {
  color: var(--c-text-3);
  opacity: 0.4;
}

.stat-highlight .stat-val {
  color: var(--c-primary);
}

/* Icon button */
.icon-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  border-radius: var(--radius);
  color: var(--c-text-2);
  cursor: pointer;
  transition: all 150ms;
}

.icon-btn:hover:not(:disabled) {
  background: var(--c-bg);
  color: var(--c-text);
}

.icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.icon-btn .material-symbols-outlined {
  font-size: 18px;
}

.spinning {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ================================================================
   Dual Panel Layout
   ================================================================ */
.dual-panel {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

.panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.panel-left {
  width: 360px;
  flex-shrink: 0;
  border-right: 1px solid var(--c-border);
  background: var(--c-surface);
}

.panel-right {
  flex: 1;
  min-width: 0;
  background: var(--c-bg);
}

/* Panel header */
.panel-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--c-border-light);
  background: var(--c-surface);
}

.panel-icon {
  font-size: 18px;
  color: var(--c-text-3);
}

.panel-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--c-text);
}

.panel-count {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--c-text-3);
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  padding: 1px 8px;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}

.filter-chip {
  padding: 3px 10px;
  font-size: 0.6875rem;
  font-weight: 500;
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  border-radius: 12px;
  cursor: pointer;
  color: var(--c-text-3);
  transition: all 150ms;
  font-family: inherit;
}

.filter-chip:hover {
  border-color: var(--c-primary);
  color: var(--c-primary);
}

.filter-chip.active {
  background: var(--c-primary-soft);
  border-color: var(--c-primary);
  color: var(--c-primary);
  font-weight: 600;
}

/* Panel search */
.panel-search {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid var(--c-border-light);
  background: var(--c-surface);
  position: relative;
}

.panel-search .si {
  font-size: 17px;
  color: var(--c-text-3);
  flex-shrink: 0;
}

.panel-search input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 6px 8px;
  font-size: 0.8125rem;
  color: var(--c-text);
  font-family: inherit;
}

.panel-search input::placeholder {
  color: var(--c-text-3);
}

.search-x {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: var(--c-text-3);
  cursor: pointer;
  border-radius: 4px;
}

.search-x:hover {
  color: var(--c-text);
  background: var(--c-border-light);
}

.search-x .material-symbols-outlined {
  font-size: 15px;
}

/* Panel body */
.panel-body {
  flex: 1;
  overflow-y: auto;
}

/* ================================================================
   Client blocks (Left panel)
   ================================================================ */
.client-block {
  border-bottom: 1px solid var(--c-border-light);
  transition: background 120ms;
}

.client-block.selected {
  background: var(--c-primary-soft);
}

.client-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: inherit;
  transition: background 120ms;
}

.client-row:hover {
  background: var(--c-bg);
}

.client-block.selected .client-row:hover {
  background: rgba(22, 163, 74, 0.06);
}

.avatar-sm {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--c-primary-muted);
  color: var(--c-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: 0.03em;
}

.client-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.client-name {
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.client-meta {
  font-size: 0.6875rem;
  color: var(--c-text-3);
}

.config-pill {
  font-size: 0.625rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  flex-shrink: 0;
}

.config-pill.configured {
  background: var(--c-primary-soft);
  color: var(--c-primary);
}

.config-pill.partial {
  background: rgba(245, 158, 11, 0.1);
  color: var(--c-warn);
}

.config-pill.pending {
  background: var(--c-border-light);
  color: var(--c-text-3);
}

.chevron {
  font-size: 18px;
  color: var(--c-text-3);
  transition: transform 200ms;
  flex-shrink: 0;
}

.chevron.open {
  transform: rotate(90deg);
}

/* Sites */
.sites-list {
  background: var(--c-bg);
  border-top: 1px solid var(--c-border-light);
}

.site-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 8px 44px;
  font-size: 0.8125rem;
  transition: background 100ms;
}

.site-row:hover {
  background: rgba(0, 0, 0, 0.02);
}

.site-icon {
  font-size: 16px;
  color: var(--c-text-3);
}

.site-name {
  flex: 1;
  color: var(--c-text-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.site-sms {
  font-family: var(--mono);
  font-size: 0.6875rem;
  color: var(--c-text-3);
  flex-shrink: 0;
}

/* ================================================================
   User table (Right panel)
   ================================================================ */
.user-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.user-table th {
  text-align: left;
  padding: 8px 16px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--c-text-3);
  background: var(--c-surface);
  border-bottom: 1px solid var(--c-border);
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.th-user { padding-left: 20px; }
.th-clients { width: 180px; }
.th-sms { width: 70px; }
.th-start, .th-end { width: 120px; }
.th-actions { width: 80px; }

.user-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--c-border-light);
  vertical-align: middle;
  background: var(--c-surface);
}

.user-table tr:last-child td {
  border-bottom: none;
}

.user-table tr {
  transition: background 100ms;
}

.user-table tr:hover td {
  background: var(--c-bg);
}

.user-table tr.modified td {
  background: rgba(22, 163, 74, 0.03);
}

.td-user {
  padding-left: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--c-border);
  flex-shrink: 0;
}

.user-dot.active {
  background: var(--c-primary);
}

.user-id {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.user-name {
  font-weight: 500;
  color: var(--c-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 0.6875rem;
  color: var(--c-text-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Client tags in user row */
.client-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.client-tag {
  font-size: 0.625rem;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 4px;
  background: var(--c-border-light);
  color: var(--c-text-3);
  white-space: nowrap;
  transition: all 150ms;
}

.client-tag.highlight {
  background: var(--c-primary-soft);
  color: var(--c-primary);
  font-weight: 600;
}

.td-sms {
  text-align: left;
}

.sms-count {
  font-family: var(--mono);
  font-size: 0.75rem;
  color: var(--c-text-3);
}

/* Time inputs */
.time-input {
  width: 96px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--c-border);
  border-radius: 6px;
  font-family: var(--mono);
  font-size: 0.8125rem;
  color: var(--c-text);
  background: var(--c-surface);
  outline: none;
  transition: border-color 150ms, box-shadow 150ms;
}

.time-input:focus {
  border-color: var(--c-primary);
  box-shadow: 0 0 0 2px var(--c-primary-soft);
}

.time-input:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Action buttons */
.td-actions {
  white-space: nowrap;
}

.btn-save,
.btn-reset {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  vertical-align: middle;
  transition: all 120ms;
}

.btn-save .material-symbols-outlined,
.btn-reset .material-symbols-outlined {
  font-size: 16px;
}

.btn-save {
  background: var(--c-primary);
  color: white;
  margin-right: 4px;
}

.btn-save:hover:not(:disabled) {
  filter: brightness(0.9);
}

.btn-save:disabled {
  background: var(--c-border-light);
  color: var(--c-text-3);
  cursor: not-allowed;
}

.btn-reset {
  background: none;
  color: var(--c-text-3);
}

.btn-reset:hover:not(:disabled) {
  background: var(--c-border-light);
  color: var(--c-text);
}

.btn-reset:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ================================================================
   Empty / No results
   ================================================================ */
.empty-row {
  padding: 20px 16px;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--c-text-3);
}

.no-results-sm {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 24px;
  text-align: center;
  color: var(--c-text-3);
  font-size: 0.8125rem;
}

.no-results-sm .material-symbols-outlined {
  font-size: 28px;
  opacity: 0.35;
}

.btn-text {
  border: none;
  background: none;
  color: var(--c-primary);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  font-family: inherit;
}

.btn-text:hover {
  text-decoration: underline;
}

/* ================================================================
   Loading
   ================================================================ */
.loading-state {
  align-items: center;
  justify-content: center;
}

.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: var(--c-text-3);
  font-size: 0.875rem;
}

.loader-spinner {
  width: 28px;
  height: 28px;
  border: 2px solid var(--c-border);
  border-top-color: var(--c-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ================================================================
   Toasts
   ================================================================ */
.toast-stack {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 9999;
}

.toast {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
  font-size: 0.8125rem;
  min-width: 260px;
  max-width: 380px;
}

.toast-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.toast.success .toast-icon { color: var(--c-primary); }
.toast.error .toast-icon { color: var(--c-danger); }
.toast.warning .toast-icon { color: var(--c-warn); }
.toast.info .toast-icon { color: var(--c-info); }

.toast-msg {
  flex: 1;
  font-weight: 500;
  color: var(--c-text);
}

.toast-dismiss {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: var(--c-text-3);
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;
}

.toast-dismiss:hover {
  background: var(--c-border-light);
  color: var(--c-text);
}

.toast-dismiss .material-symbols-outlined {
  font-size: 15px;
}

/* ================================================================
   Transitions
   ================================================================ */
.slide-enter-active,
.slide-leave-active {
  transition: all 200ms ease;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  max-height: 0;
}

.slide-enter-to,
.slide-leave {
  max-height: 2000px;
}

.toast-anim-enter-active,
.toast-anim-leave-active {
  transition: all 250ms ease;
}

.toast-anim-enter,
.toast-anim-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* ================================================================
   Responsive
   ================================================================ */
@media (max-width: 1100px) {
  .topbar-stats {
    display: none;
  }

  .th-clients, .td-clients {
    display: none;
  }
}

@media (max-width: 860px) {
  .dual-panel {
    flex-direction: column;
  }

  .panel-left {
    width: 100%;
    max-height: 260px;
    border-right: none;
    border-bottom: 1px solid var(--c-border);
  }

  .panel-right {
    flex: 1;
  }

  .panel-actions {
    display: none;
  }

  .topbar {
    flex-wrap: wrap;
    height: auto;
    padding: 12px 16px;
    gap: 10px;
  }

  .topbar-right {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 560px) {
  .user-table {
    display: block;
  }

  .user-table thead {
    display: none;
  }

  .user-table tbody,
  .user-table tr,
  .user-table td {
    display: block;
    width: 100%;
  }

  .user-table tr {
    padding: 12px 16px;
    border-bottom: 1px solid var(--c-border-light);
  }

  .user-table td {
    padding: 4px 0;
    border: none;
  }

  .td-user {
    padding-left: 0;
  }

  .td-actions {
    padding-top: 8px;
  }

  .th-sms, .td-sms {
    display: none;
  }
}

.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
</style>
